import { ma, NodeType, truncateSerializedSessionHistory } from '@lemonade-hq/chat-engine';
import type { FinishNodeState, RenderNodeState, Schema } from '@lemonade-hq/chat-engine';
import { combineStepsAnswers } from '@lemonade-hq/chat-wizard-client-base';
import type { UniclientStep } from '@lemonade-hq/uniclient-chat';
import { isAllowedToTransform, useTheme } from '@lemonade-hq/uniclient-theme';
import set from 'lodash/set';
import { useCallback, useMemo } from 'react';
import type { ChatEngineSession } from './chat_engine.client';
import type {
  WizardExtendedStepWithPersistenceAndId,
  WizardPersistenceContextValue,
} from './wizard_persistence.context';

export function usePersistenceHelpers<TPresentation = undefined>({
  history,
  currentStepIndex,
  session,
  setSession,
}: {
  readonly history?: (FinishNodeState | RenderNodeState<TPresentation>)[];
  readonly currentStepIndex: number;
  readonly session: ChatEngineSession<TPresentation> | null;
  readonly setSession: (value: React.SetStateAction<ChatEngineSession<TPresentation> | null>) => void;
}): Pick<
  WizardPersistenceContextValue<UniclientStep>,
  'answersSchema' | 'persistedAnswersByStep' | 'steps' | 'truncateObsoleteLocalSteps'
> {
  const { theme, getThemeImageUrl } = useTheme();

  const replaceAssetsUrls = useCallback(
    <T>(value: T): T => {
      if (theme.brand === 'pinkish' && theme.scheme === 'light') {
        return value;
      }

      if (typeof value === 'string' && isAllowedToTransform(value)) {
        return getThemeImageUrl(value) as unknown as T;
      }

      if (Array.isArray(value)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        const mapped = value.map(item => replaceAssetsUrls(item));
        return mapped as unknown as T;
      }

      if (value != null && typeof value === 'object') {
        const result = { ...value } as T;

        for (const [key, val] of Object.entries(value)) {
          (result as Record<string, unknown>)[key] = replaceAssetsUrls(val);
        }

        return result;
      }

      return value;
    },
    [getThemeImageUrl, theme.brand, theme.scheme],
  );
  const renderSteps = useMemo(() => history?.filter(step => step.type === NodeType.Render) ?? [], [history]);

  const steps = useMemo<WizardExtendedStepWithPersistenceAndId<UniclientStep>[]>(() => {
    return renderSteps.map(step => {
      const rendered = step.renderedStep as unknown as UniclientStep;
      const output = (rendered as unknown as { data?: { output?: string } }).data?.output;

      return replaceAssetsUrls({
        ...rendered,
        id: step.id,
        name: step.name,
        output,
        persistedAnswers: step.answers,
      } as WizardExtendedStepWithPersistenceAndId<UniclientStep>);
    });
  }, [renderSteps, replaceAssetsUrls]);

  const truncateObsoleteLocalSteps = useCallback(
    /* istanbul ignore next -- can't mock */
    (newCurrentStepIndex: number = currentStepIndex) => {
      /* istanbul ignore next -- can't mock */
      setSession(prevSession =>
        /* istanbul ignore next -- can't mock */
        truncateSerializedSessionHistory({
          session: prevSession,
          answeredNodeIndexInHistory: newCurrentStepIndex,
        }),
      );
    },
    [currentStepIndex, setSession],
  );

  const persistedAnswersByStep = useMemo(() => {
    const answersByStep = renderSteps.map(step => step.answers ?? {});
    const initialValuesByStep = steps.map(step => {
      const initialValues = step.data?.initialValues as Record<string, unknown> | undefined;
      return initialValues == null
        ? {}
        : Object.keys(initialValues).reduce(
            (hash, flatAttribute) => {
              return set(hash, flatAttribute, initialValues[flatAttribute]);
            },
            {} as Record<string, unknown>,
          );
    });
    return combineStepsAnswers(initialValuesByStep, answersByStep);
  }, [renderSteps, steps]);

  const answersSchema = useMemo(() => {
    return (session?.schema ?? ma.record({})) as Schema;
  }, [session?.schema]);

  return { steps, truncateObsoleteLocalSteps, persistedAnswersByStep, answersSchema };
}
